import { Component, HostListener, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterOutlet } from '@angular/router';
import { OAuthModule } from 'angular-oauth2-oidc';
import { filter } from 'rxjs/internal/operators/filter';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map, mergeMap } from 'rxjs';
import { AppLoadingService } from './app-loading.service';
import EnvService from './app.env';
import { routes } from './app.routes';
import { Translation, getUserLang } from './i18n/i18n';
import { AuthService } from './auth/auth.service';
import { PublicClient } from './openapi/openapi';
import { RemoteConfigService } from './remote-config/remote-config.service';
import { AppIconRegister } from './app-icon.register';
import { PictureAcquisitionService } from './picture-acquisition/picture-acquistion.service';
import { MobileModeService } from './app-mobile-mode.service';

@Component({
  selector: 'app-root',
  standalone: true,
  providers: [EnvService, PublicClient],
  imports: [
    RouterOutlet, MatButtonModule, CommonModule, MatIconModule, MatProgressBarModule, TranslatePipe, OAuthModule,
  ],
  styleUrls: ['./app.component.scss'],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  title = 'alix-spa';

  currentRouteName = signal('');

  scrolling = signal(false);

  logo = signal('');

  toggleMenuMobile = signal(false);

  public appLoading = inject(AppLoadingService);

  public remoteConfig = inject(RemoteConfigService);

  public env = inject(EnvService);

  public translate = inject(TranslateService);

  public readonly mobileModeService = inject(MobileModeService);

  private readonly route = inject(ActivatedRoute);

  private readonly router = inject(Router);

  private readonly authService = inject(AuthService);

  private readonly iconRegister = inject(AppIconRegister);

  private readonly acquisitionService = inject(PictureAcquisitionService);

  get isReady() {
    return this.authService.isLoggedIn && this.remoteConfig.isLoaded;
  }

  ngOnInit() {
    this.initApp();
  }

  private async initApp() {
    this.appLoading.add('app');

    this.logo.set(this.env.value('CUSTOMER_LOGO'));

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.route),
      map((activatedRoute) => {
        let route = activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }

        return route;
      }),
      mergeMap((route) => route.data),
    ).subscribe((data) => {
      this.currentRouteName.set(data?.['name'] || '');
    });

    this.setDefaultLanguage();

    try {
      await this.authService.authenticate();
    } catch (_err) {}

    if (this.authService.isLoggedIn) {
      await this.remoteConfig.load();
      this.acquisitionService.nbPictures = this.remoteConfig.get('tablet_nb_pictures') as number || 8;
    }

    this.iconRegister.register();

    this.appLoading.remove('app');
  }

  setDefaultLanguage() {
    const lang = getUserLang();
    this.translate.setFallbackLang(lang);
    this.useLangage(lang)
  }

  @HostListener('body:scroll', [])
  onWindowScroll() {
    this.scrolling.set(document.body.scrollTop > 0);
  }

  goTo(page: keyof typeof routes) {
    const queryParams: Params = {};
    if (page !== 'tablet') {
      queryParams['pageSize'] = 10;
    }

    if (this.mobileModeService.isMobileMode()) {
      queryParams['mobile'] = true;
    }
    this.router.navigate([routes[page].children ? routes[page].children?.[0].url?.(1) : routes[page].url?.(1)], {
      queryParams,
    }).finally(() => {
      this.toggleMenuMobile.set(false);
    });
  }

  useLangage(lang: Translation) {
    localStorage.setItem('lang', lang);
    this.translate.use(lang);
    window.dispatchEvent(new CustomEvent('langChanged', { detail: lang }))
  }

  triggerMobileMenu() {
    this.toggleMenuMobile.set(!this.toggleMenuMobile());
  }
}
